// ===========================================
// Custom Role React Query Hooks
// ===========================================
// Hooks for managing RBAC custom roles and staff role assignments

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  customRoleApi,
  CreateCustomRoleInput,
  UpdateCustomRoleInput,
  AssignStaffRoleInput,
  UpdateStaffRoleInput,
  StaffListParams,
  OfficeAccessInput,
} from '../api/customRole.api';
import { userKeys } from './useUsers';
import { enquiryKeys } from './useEnquiries';

// ===========================================
// QUERY KEYS
// ===========================================

export const customRoleKeys = {
  all: ['customRoles'] as const,
  lists: () => [...customRoleKeys.all, 'list'] as const,
  list: () => [...customRoleKeys.lists()] as const,
  details: () => [...customRoleKeys.all, 'detail'] as const,
  detail: (id: string) => [...customRoleKeys.details(), id] as const,
  permissions: () => [...customRoleKeys.all, 'permissions'] as const,
  staff: ['staffRoles'] as const,
  staffLists: () => [...customRoleKeys.staff, 'list'] as const,
  staffList: (params?: StaffListParams) => [...customRoleKeys.staffLists(), params] as const,
  admins: ['schoolAdmins'] as const,
};

// ===========================================
// CUSTOM ROLE QUERY HOOKS
// ===========================================

/**
 * List all custom roles for the school
 */
export function useCustomRoles() {
  return useQuery({
    queryKey: customRoleKeys.list(),
    queryFn: () => customRoleApi.getCustomRoles(),
  });
}

/**
 * Get a single custom role with full permissions and staff list
 */
export function useCustomRole(roleId: string, openCount = 0, enabled = true) {
  return useQuery({
    // The role editor seeds from this (roles plan §5.8, F2), so every OPEN must read the server,
    // never a cached copy: Save writes the whole permission set back, and a copy older than
    // another admin's change would silently undo it. `openCount` makes each open its own cache
    // entry, so it starts empty and fetches. (`refetchOnMount: 'always'` did nothing here: the
    // observer mounts once with the page, and the app-wide staleTime is 5 minutes.) The update
    // mutation's `detail(roleId)` invalidation still matches it by prefix.
    queryKey: [...customRoleKeys.detail(roleId), openCount] as const,
    queryFn: () => customRoleApi.getCustomRole(roleId),
    // `enabled` lets the page observe the detail only while the editor is open, so a role
    // deleted later is never refetched (404 retries) by a leftover observer.
    enabled: !!roleId && enabled,
  });
}

/**
 * Get the available permissions catalogue (groups, resources, actions)
 */
export function useAvailablePermissions() {
  return useQuery({
    queryKey: customRoleKeys.permissions(),
    queryFn: () => customRoleApi.getAvailablePermissions(),
    staleTime: Infinity, // Static data - never re-fetch
  });
}

// ===========================================
// CUSTOM ROLE MUTATION HOOKS
// ===========================================

/**
 * Create a new custom role
 */
export function useCreateCustomRole() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: CreateCustomRoleInput) => customRoleApi.createCustomRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customRoleKeys.all });
      enqueueSnackbar('Role created successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to create role';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Update an existing custom role
 */
export function useUpdateCustomRole() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ roleId, data }: { roleId: string; data: UpdateCustomRoleInput }) =>
      customRoleApi.updateCustomRole(roleId, data),
    onSuccess: (_, { roleId }) => {
      queryClient.invalidateQueries({ queryKey: customRoleKeys.all });
      queryClient.invalidateQueries({ queryKey: customRoleKeys.detail(roleId) });
      // The Staff Members list shows each role's name and colour (#399).
      queryClient.invalidateQueries({ queryKey: customRoleKeys.staff });
      enqueueSnackbar('Role updated successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to update role';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Delete a custom role
 */
export function useDeleteCustomRole() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (roleId: string) => customRoleApi.deleteCustomRole(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customRoleKeys.all });
      queryClient.invalidateQueries({ queryKey: customRoleKeys.staff }); // #399
      enqueueSnackbar('Role deleted', { variant: 'info' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to delete role';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// STAFF ROLE QUERY HOOKS
// ===========================================

/**
 * List staff members with their assigned roles
 */
export function useStaffMembers(params?: StaffListParams) {
  return useQuery({
    queryKey: customRoleKeys.staffList(params),
    queryFn: () => customRoleApi.getStaffMembers(params),
  });
}

// ===========================================
// STAFF ROLE MUTATION HOOKS
// ===========================================

/**
 * Assign a custom role to a user (upsert if already assigned)
 */
export function useAssignStaffRole() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: AssignStaffRoleInput) => customRoleApi.assignStaffRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customRoleKeys.staff });
      queryClient.invalidateQueries({ queryKey: customRoleKeys.all });
      // The teachers list's access chip and Edit Teacher's Office access read the staff role, and a
      // role without enquiries.read releases the person's enquiries (/qa L4).
      queryClient.invalidateQueries({ queryKey: userKeys.teachers() });
      queryClient.invalidateQueries({ queryKey: enquiryKeys.all });
      enqueueSnackbar('Staff role assigned', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to assign staff role';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Update an existing staff role assignment
 */
export function useUpdateStaffRole() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateStaffRoleInput }) =>
      customRoleApi.updateStaffRole(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customRoleKeys.staff });
      queryClient.invalidateQueries({ queryKey: customRoleKeys.all });
      queryClient.invalidateQueries({ queryKey: userKeys.teachers() });
      queryClient.invalidateQueries({ queryKey: enquiryKeys.all });
      enqueueSnackbar('Staff role updated', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to update staff role';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Revoke a staff role from a user
 */
export function useRevokeStaffRole() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (userId: string) => customRoleApi.revokeStaffRole(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customRoleKeys.staff });
      queryClient.invalidateQueries({ queryKey: customRoleKeys.all });
      queryClient.invalidateQueries({ queryKey: userKeys.teachers() });
      queryClient.invalidateQueries({ queryKey: enquiryKeys.all });
      enqueueSnackbar('Staff role revoked', { variant: 'info' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to revoke staff role';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Move a person between Admin, a staff role and neither (roles slice 3, §7.3). Every list that
 * shows who is an Admin or holds a role changes: the teachers list (its access chip), the staff
 * list, the role counts and the Admins list.
 */
export function useSetOfficeAccess() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: OfficeAccessInput }) =>
      customRoleApi.setOfficeAccess(userId, data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: userKeys.teachers() }),
        queryClient.invalidateQueries({ queryKey: customRoleKeys.staff }),
        queryClient.invalidateQueries({ queryKey: customRoleKeys.all }),
        queryClient.invalidateQueries({ queryKey: customRoleKeys.admins }),
        // A move out of the office releases the person's enquiries (D-S6, /qa L4).
        queryClient.invalidateQueries({ queryKey: enquiryKeys.all }),
      ]);
      enqueueSnackbar('Office access changed', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to change office access';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// ADMINS AND SUPPORT ACCOUNTS (roles slice 2, R13)
// ===========================================

/**
 * The school's Admins with their support-account flag
 */
export function useSchoolAdmins() {
  return useQuery({
    queryKey: customRoleKeys.admins,
    queryFn: () => customRoleApi.getAdmins(),
  });
}

/**
 * Mark or unmark an Admin as a support account
 */
export function useSetSupportAccount() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ userId, value }: { userId: string; value: boolean }) =>
      customRoleApi.setSupportAccount(userId, value),
    // Awaited, so the checkbox stays disabled until the list shows the new value (/qa I8).
    onSuccess: async (admin) => {
      await queryClient.invalidateQueries({ queryKey: customRoleKeys.admins });
      enqueueSnackbar(
        admin.isSupportAccount
          ? `${admin.firstName} is now a support account`
          : `${admin.firstName} is no longer a support account`,
        { variant: 'success' }
      );
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to change the support account';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}
